import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { initializeSentry, SentryErrorBoundary } from '@glomopay/sentry';
import { AppEnvMode } from '@glomopay/utils';

import '@/config/env';
import { routeTree } from './routeTree.gen';

import { queryClient } from '@/config/query-client';
import { OTEL_ENABLED } from '@/constants/telemetry';
import { SENTRY_URL, INVESTOR_PORTAL_SESSION_ID_TAG_KEY, INVESTOR_PORTAL_COMPONENT_TAG } from '@/constants/sentry';
import { initializeOpenTelemetry } from '@/config/opentelemetry.config';
import { enrichmentContext } from '@/lib/telemetry/enrichment-context';
import { ErrorPage } from '@/shared/error-page/error-page';
import { QueryProvider } from '@/shared/query-provider/query-provider';
import { setLoginRedirect } from '@/shared/router/public';
import { TenantProvider } from '@/features/tenant/public';
import { TenantThemeWrapper } from '@/features/tenant-theme/public';
import { InvestorColorSchemeProvider } from '@/features/color-scheme/public';
import { captureHandoffPayload } from '@/features/investor-entry/public';
import { hasLiveInvestorSession, readLiveSessionParentMerchantId } from '@/features/investor-login/public';
import { analytics, AnalyticsEventName } from '@/features/analytics/public';
import './index.css';

const envMode = import.meta.env.MODE;
if (envMode === AppEnvMode.PRODUCTION || envMode === AppEnvMode.STAGING || envMode === AppEnvMode.SANDBOX) {
  initializeSentry({
    dsn: SENTRY_URL,
    environment: envMode,
    sessionId: enrichmentContext.getSessionId(),
    sessionIdTagKey: INVESTOR_PORTAL_SESSION_ID_TAG_KEY,
    shouldCaptureConsoleErrors: false,
    tags: { component: INVESTOR_PORTAL_COMPONENT_TAG },
  });
}

if (OTEL_ENABLED) {
  initializeOpenTelemetry();
}

// After telemetry registers, or the entry log is dropped; before the router reads
// window.location, so the blob leaves the address bar first.
const handoffPayload = captureHandoffPayload();

analytics.setContext({ parent_merchant_id: readLiveSessionParentMerchantId() });
analytics.track(AnalyticsEventName.INVESTOR_APP_LOADED, { has_handoff_payload: !!handoffPayload, has_live_session: hasLiveInvestorSession() });

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

setLoginRedirect(() => void router.navigate({ to: '/login' }));

const rootElement = document.getElementById('app');
if (rootElement && !rootElement.innerHTML) {
  createRoot(rootElement).render(
    <StrictMode>
      <SentryErrorBoundary fallback={<ErrorPage />}>
        <QueryProvider>
          <TenantProvider>
            <TenantThemeWrapper>
              <InvestorColorSchemeProvider>
                <RouterProvider router={router} />
              </InvestorColorSchemeProvider>
            </TenantThemeWrapper>
          </TenantProvider>
        </QueryProvider>
      </SentryErrorBoundary>
    </StrictMode>,
  );
}
